import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, GripVertical } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useAuth } from '@/context/AuthContext'
import * as api from '@/lib/api'
import type { AdminMember, AdminGroup } from '@/lib/api'
import RowActions from '@/components/RowActions'
import { useConfirm } from '@/components/ConfirmDialog'
import { normalizeImageKitUrl } from '@lic/media-urls'

// ─── MemberCard ──────────────────────────────────────────────────────────────

function MemberCard({
  member,
  onView,
  onEdit,
  onDelete,
  dragHandleProps,
}: {
  member: AdminMember
  onView: () => void
  onEdit: () => void
  onDelete: () => void
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>
}) {
  return (
    <div
      className="relative rounded-xl overflow-hidden border border-zinc-800 hover:border-zinc-700 cursor-pointer transition-all group"
      onClick={onView}
    >
      {/* Drag handle — top-left, only visible on hover */}
      {dragHandleProps && (
        <div
          {...dragHandleProps}
          className="absolute top-1.5 left-1.5 z-20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center w-6 h-6 rounded bg-black/30 backdrop-blur-sm text-white/60 hover:text-white cursor-grab active:cursor-grabbing"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical size={12} />
        </div>
      )}

      {/* Photo — full card, portrait */}
      <div className="aspect-[3/4] relative overflow-hidden bg-zinc-800">
        {member.image_url ? (
          <img
            src={normalizeImageKitUrl(member.image_url)}
            alt={member.stage_name}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-zinc-800">
            <span className="text-zinc-600 text-3xl font-bold">{member.stage_name[0]}</span>
          </div>
        )}

        {/* Active badge — top left (shifted right if drag handle present) */}
        <div className={`absolute top-2 z-10 ${dragHandleProps ? 'left-9' : 'left-2'}`}>
          <span
            className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full backdrop-blur-sm ${
              member.is_active
                ? 'bg-green-500/20 text-green-400 border border-green-500/20'
                : 'bg-black/30 text-white/40 border border-white/10'
            }`}
          >
            {member.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>

        {/* RowActions — top right */}
        <div className="absolute top-1.5 right-1.5 z-10" onClick={(e) => e.stopPropagation()}>
          <RowActions
            triggerClassName="flex items-center justify-center w-7 h-7 rounded bg-black/20 hover:bg-black/40 backdrop-blur-sm text-white/80 hover:text-white transition-colors"
            actions={[
              { label: 'Edit', onClick: onEdit },
              { label: 'Hapus', onClick: onDelete, variant: 'danger' },
            ]}
          />
        </div>

        {/* Glassmorphism content overlay — bottom */}
        <div className="absolute inset-x-0 bottom-0 backdrop-blur-md bg-black/30 border-t border-white/10 px-3 py-2.5">
          <p className="text-white font-semibold text-sm leading-tight truncate">{member.stage_name}</p>
          {member.real_name && (
            <p className="text-white/50 text-[11px] mt-0.5 truncate">{member.real_name}</p>
          )}
          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] font-medium text-white/60 bg-white/10 border border-white/10 px-1.5 py-0.5 rounded">
              {member.role}
            </span>
            {member.positions.length > 0 && (
              <span className="text-white/35 text-[10px] truncate">
                {member.positions.join(' · ')}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── SortableMemberCard ───────────────────────────────────────────────────────

function SortableMemberCard({
  member,
  onView,
  onEdit,
  onDelete,
}: {
  member: AdminMember
  onView: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: member.id,
  })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: 'relative',
    zIndex: isDragging ? 10 : undefined,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <MemberCard
        member={member}
        onView={onView}
        onEdit={onEdit}
        onDelete={onDelete}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  )
}

// ─── Constants ───────────────────────────────────────────────────────────────

const CARD_GRID = 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3'

// ─── MembersPage ─────────────────────────────────────────────────────────────

export default function MembersPage() {
  const { token, isGroupManager, managedGroupSlug } = useAuth()
  const navigate = useNavigate()

  const [members, setMembers] = useState<AdminMember[]>([])
  const [groups, setGroups] = useState<AdminGroup[]>([])
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { confirm, dialog } = useConfirm()

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const load = useCallback(async () => {
    if (!token) return
    setLoading(true)
    try {
      if (isGroupManager) {
        const memberList = await api.getAdminMembers(token)
        setMembers(memberList)
      } else {
        const [memberList, groupList] = await Promise.all([
          api.getAdminMembers(token),
          api.getAdminGroups(token),
        ])
        setMembers(memberList)
        setGroups(groupList)
        setExpandedGroups(new Set(groupList.map((g) => g.id)))
      }
      setError(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [token, isGroupManager])

  useEffect(() => { load() }, [load])

  const handleDelete = async (id: string, name: string) => {
    if (!await confirm(`"${name}" akan dihapus permanen dan tidak bisa dipulihkan.`, { title: `Hapus "${name}"` })) return
    if (!token) return
    try {
      await api.deleteMember(token, id)
      load()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e))
    }
  }

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) next.delete(groupId)
      else next.add(groupId)
      return next
    })
  }

  const handleDragEnd = useCallback(async (event: DragEndEvent, groupId: string) => {
    const { active, over } = event
    if (!over || active.id === over.id || !token) return

    const prevMembers = members
    const groupMems = members.filter((m) => m.group_id === groupId)
    const others = members.filter((m) => m.group_id !== groupId)
    const oldIndex = groupMems.findIndex((m) => m.id === String(active.id))
    const newIndex = groupMems.findIndex((m) => m.id === String(over.id))
    const reordered = arrayMove(groupMems, oldIndex, newIndex)

    setMembers([...others, ...reordered])

    try {
      await api.patchMembersOrder(token, groupId, reordered.map((m) => m.id))
    } catch {
      setMembers(prevMembers)
      alert('Gagal menyimpan urutan. Silakan coba lagi.')
    }
  }, [members, token])

  if (loading) {
    return <div className="p-6"><p className="text-zinc-500 text-sm">Loading…</p></div>
  }

  if (error) {
    return <div className="p-6"><p className="text-red-400 text-sm">{error}</p></div>
  }

  /* ── Group Manager: flat card grid with drag & drop ──────────────── */
  if (isGroupManager) {
    const myMembers = managedGroupSlug
      ? members.filter((m) => m.group_slug === managedGroupSlug)
      : members
    const managedGroupId = myMembers[0]?.group_id ?? ''

    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-lg font-semibold text-white">Members</h1>
            <p className="text-zinc-500 text-xs mt-0.5">{myMembers.length} members</p>
          </div>
          <button
            onClick={() => navigate('/members/create')}
            className="bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-semibold px-4 py-2 rounded transition-colors"
          >
            + Add Member
          </button>
        </div>

        {myMembers.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-zinc-500 text-sm">Belum ada member.</p>
            <button
              onClick={() => navigate('/members/create')}
              className="mt-3 text-xs text-yellow-400 hover:text-yellow-300 transition-colors"
            >
              + Tambah member pertama
            </button>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(e) => handleDragEnd(e, managedGroupId)}
          >
            <SortableContext items={myMembers.map((m) => m.id)} strategy={rectSortingStrategy}>
              <div className={CARD_GRID}>
                {myMembers.map((member) => (
                  <SortableMemberCard
                    key={member.id}
                    member={member}
                    onView={() => navigate(`/members/${member.id}`, { state: { member } })}
                    onEdit={() => navigate(`/members/${member.id}/edit`, { state: { member } })}
                    onDelete={() => handleDelete(member.id, member.stage_name)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
        {dialog}
      </div>
    )
  }

  /* ── Admin / Superadmin: accordion per group with drag & drop ────── */
  const membersByGroup: Record<string, AdminMember[]> = {}
  for (const m of members) {
    ;(membersByGroup[m.group_slug] ??= []).push(m)
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-semibold text-white">Members</h1>
          <p className="text-zinc-500 text-xs mt-0.5">
            {members.length} members · {groups.length} groups
          </p>
        </div>
      </div>

      {groups.length === 0 && (
        <div className="py-16 text-center">
          <p className="text-zinc-500 text-sm">Belum ada group.</p>
        </div>
      )}

      <div className="space-y-6">
        {groups.map((group) => {
          const groupMembers = membersByGroup[group.slug] ?? []
          const isExpanded = expandedGroups.has(group.id)

          return (
            <div key={group.id}>
              {/* Accordion header */}
              <div className="flex items-center gap-3 mb-3">
                <button
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  className="flex items-center gap-2.5 flex-1 min-w-0 text-left py-1 group/header"
                >
                  <div
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ background: group.color_primary || '#3f3f46' }}
                  />
                  <span className="font-semibold text-white text-sm group-hover/header:text-zinc-200 transition-colors">
                    {group.name}
                  </span>
                  <span className="text-zinc-600 text-xs flex-shrink-0">
                    {groupMembers.length} members
                  </span>
                  <ChevronDown
                    size={14}
                    className={`text-zinc-500 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                  />
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/members/create', { state: { initialGroupSlug: group.slug } })}
                  className="text-[11px] text-zinc-500 hover:text-yellow-400 transition-colors flex-shrink-0 px-2 py-1 rounded hover:bg-zinc-800"
                >
                  + Add
                </button>
              </div>

              <div className="h-px bg-zinc-800 mb-4" />

              {isExpanded && (
                groupMembers.length === 0 ? (
                  <p className="text-zinc-600 text-xs py-4 px-1">Belum ada member di group ini.</p>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(e) => handleDragEnd(e, group.id)}
                  >
                    <SortableContext items={groupMembers.map((m) => m.id)} strategy={rectSortingStrategy}>
                      <div className={CARD_GRID}>
                        {groupMembers.map((member) => (
                          <SortableMemberCard
                            key={member.id}
                            member={member}
                            onView={() => navigate(`/members/${member.id}`, { state: { member } })}
                            onEdit={() => navigate(`/members/${member.id}/edit`, { state: { member } })}
                            onDelete={() => handleDelete(member.id, member.stage_name)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )
              )}
            </div>
          )
        })}
      </div>

      {dialog}
    </div>
  )
}
